import { useEffect, useCallback } from 'react';
import { registerDeviceToken, unregisterDeviceToken } from '../lib/pushNotifications';

interface PushNotificationBridge {
  onTokenReceived?: (token: string, platform: string) => void;
  onNotificationTapped?: (data: Record<string, string>) => void;
}

declare global {
  interface Window {
    PushNotificationBridge?: PushNotificationBridge;
    registerPushToken?: (token: string, platform: string, deviceName?: string, appVersion?: string) => void;
    unregisterPushToken?: (token: string) => void;
  }
}

export function usePushNotifications() {
  const handleTokenRegistration = useCallback(
    async (token: string, platform: string, deviceName?: string, appVersion?: string) => {
      try {
        await registerDeviceToken(
          token,
          platform as 'ios' | 'android' | 'web',
          deviceName,
          appVersion
        );
        localStorage.setItem('device_token_registered', 'true');
        localStorage.setItem('device_push_token', token);
      } catch (err) {
        console.error('Failed to register push token:', err);
      }
    },
    []
  );

  const handleTokenUnregistration = useCallback(async (token: string) => {
    try {
      await unregisterDeviceToken(token);
    } catch (err) {
      console.error('Failed to unregister push token:', err);
    }
  }, []);

  useEffect(() => {
    // Expose global functions for native apps to call
    window.registerPushToken = handleTokenRegistration;
    window.unregisterPushToken = handleTokenUnregistration;

    // Set up bridge for native app communication
    window.PushNotificationBridge = {
      onTokenReceived: (token: string, platform: string) => {
        handleTokenRegistration(token, platform);
      },
      onNotificationTapped: (data: Record<string, string>) => {
        if (data.action_url) {
          window.location.href = data.action_url;
        }
      },
    };

    return () => {
      delete window.registerPushToken;
      delete window.unregisterPushToken;
      delete window.PushNotificationBridge;
    };
  }, [handleTokenRegistration, handleTokenUnregistration]);
}
